import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import portalT from '../translations/portal';
import axios from 'axios';
import { ArrowLeft, Zap, Loader } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ExportPDFButton from '../components/ExportPDFButton';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ProcessOptimizer = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.processOpt;

  const [formData, setFormData] = useState({
    process_name: '', current_process_description: '', pain_points: '',
    team_size: '1-10', frequency: 'Daily',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tools/process-optimizer`, formData, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const teamOpts = lang === 'nl'
    ? [['1-10','1-10 personen'],['11-50','11-50 personen'],['51-100','51-100 personen'],['100+','100+ personen']]
    : [['1-10','1-10 people'],['11-50','11-50 people'],['51-100','51-100 people'],['100+','100+ people']];

  const freqOpts = lang === 'nl'
    ? [['Hourly','Per uur'],['Daily','Dagelijks'],['Weekly','Wekelijks'],['Monthly','Maandelijks'],['Occasionally','Af en toe']]
    : [['Hourly','Hourly'],['Daily','Daily'],['Weekly','Weekly'],['Monthly','Monthly'],['Occasionally','Occasionally']];

  return (
    <div className="tool-page">
      <div className="tool-header">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" data-testid="back-btn"><ArrowLeft size={16} /> {t.common.aiTools}</button>
        <h1 className="heading-1" data-testid="process-title">{tp.title}</h1>
      </div>
      <div className="tool-content">
        {!result ? (
          <div className="tool-form-container">
            <div className="tool-intro"><Zap size={32} /><h2 className="heading-2">{tp.intro}</h2><p className="body-medium text-secondary">{tp.introDesc}</p></div>
            <form onSubmit={handleSubmit} className="tool-form">
              <div className="form-group"><label className="body-small">{tp.processName}</label><input type="text" name="process_name" value={formData.process_name} onChange={handleChange} required className="form-input" placeholder={tp.processPlaceholder} data-testid="input-process" /></div>
              <div className="form-group"><label className="body-small">{tp.currentProcess}</label><textarea name="current_process_description" value={formData.current_process_description} onChange={handleChange} required rows="6" className="form-input" placeholder={tp.currentPlaceholder} data-testid="input-current" /></div>
              <div className="form-group"><label className="body-small">{tp.painPoints}</label><textarea name="pain_points" value={formData.pain_points} onChange={handleChange} required rows="4" className="form-input" placeholder={tp.painPlaceholder} data-testid="input-pain" /></div>
              <div className="form-row">
                <div className="form-group"><label className="body-small">{tp.teamSize}</label><select name="team_size" value={formData.team_size} onChange={handleChange} className="form-input" data-testid="select-team">{(Array.isArray(teamOpts) ? teamOpts : []).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                <div className="form-group"><label className="body-small">{tp.frequency}</label><select name="frequency" value={formData.frequency} onChange={handleChange} className="form-input" data-testid="select-freq">{(Array.isArray(freqOpts) ? freqOpts : []).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary submit-btn" data-testid="submit-btn">{loading ? <><Loader className="spin" size={16} /> {tp.analyzing}</> : <><Zap size={16} /> {tp.optimize}</>}</button>
            </form>
          </div>
        ) : (
          <div className="tool-result">
            <div className="result-header"><h2 className="heading-2">{tp.resultTitle}</h2><div style={{display:'flex',gap:'0.5rem'}}><ExportPDFButton title={tp.resultTitle} content={result.optimization_plan} toolName={tp.title} /><button onClick={() => setResult(null)} className="btn-secondary" data-testid="analyze-another-btn">{tp.analyzeAnother}</button></div></div>
            <div className="result-content markdown-content"><ReactMarkdown>{result.optimization_plan}</ReactMarkdown></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessOptimizer;
